import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronRight, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";

import TabsCst from "@components/TabsCst";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { CorporateBrandAssetDto, CorporateBrandAssetRequest } from "../types";
import { InvalidBrandAssetZipError } from "../services";
import { useBrandAssetEditor } from "../hooks/useBrandAssetEditor";
import ColorsPanel from "./ColorsPanel";
import LogosPanel from "./LogosPanel";
import ParametersPanel from "./ParametersPanel";

type BrandAssetTab = "colors" | "logos" | "parameters";

interface ModelEditorDialogProps {
  open: boolean;
  mode: "create" | "edit";
  editingDto?: CorporateBrandAssetDto;
  corporateId: number;
  isGlobalScope: boolean;
  sourceModels: CorporateBrandAssetDto[];
  corporateName: string;
  globalCorporateName: string;
  createBrandAsset: (body: CorporateBrandAssetRequest) => Promise<unknown>;
  editBrandAsset: (
    id: number,
    body: CorporateBrandAssetRequest,
  ) => Promise<unknown>;
  onClose: () => void;
  onSaved: () => void;
}

const ModelEditorDialog: React.FC<ModelEditorDialogProps> = ({
  open,
  mode,
  editingDto,
  corporateId,
  isGlobalScope,
  sourceModels,
  corporateName,
  globalCorporateName,
  createBrandAsset,
  editBrandAsset,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(["corporate-brand-asset", "common"]);
  const [tab, setTab] = useState<BrandAssetTab>("colors");
  const [sourceId, setSourceId] = useState<number | "">("");
  const [uploadedZipName, setUploadedZipName] = useState("");
  const zipInputRef = useRef<HTMLInputElement>(null);
  const [feedback, setFeedback] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  const editor = useBrandAssetEditor({
    corporateId,
    corporateName,
    globalCorporateName,
    createBrandAsset,
    editBrandAsset,
  });

  const {
    name,
    setName,
    content,
    originalContent,
    setColor,
    removeColor,
    setLogo,
    loadFrom,
    loadFromZipFile,
    loadingSource,
    submitting,
    save,
    saveToGlobal,
  } = editor;

  useEffect(() => {
    if (!open) return;
    setTab("colors");
    setFeedback(null);
    if (mode === "edit" && editingDto) {
      setName(editingDto.name);
      setSourceId(editingDto.id);
      loadFrom(editingDto);
    } else {
      setName("");
      setSourceId("");
      loadFrom(null);
    }
  }, [open, mode, editingDto?.id]);

  const handlePickSource = (id: number) => {
    setSourceId(id);
    const source = sourceModels.find((m) => m.id === id);
    if (source) loadFrom(source);
  };

  const handlePickZipFile = async (file?: File) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) {
      setFeedback({ severity: "error", message: t("editor.zipInvalidFormat") });
      return;
    }
    try {
      await loadFromZipFile(file);
      setSourceId("");
    } catch (e) {
      setFeedback({
        severity: "error",
        message:
          e instanceof InvalidBrandAssetZipError
            ? t("editor.zipMissingVariables", {
                count: e.missing.length,
                list: e.missing.join(", "),
              })
            : t("editor.zipReadError"),
      });
    }
  };

  const handleSave = async () => {
    try {
      await save(mode === "edit" ? editingDto?.id : undefined);
      onSaved();
    } catch (e) {
      setFeedback({ severity: "error", message: t("feedback.saveError") });
    }
  };

  const handleSaveToGlobal = async () => {
    try {
      await saveToGlobal();
      setFeedback({ severity: "success", message: t("feedback.globalSaved") });
    } catch (e) {
      setFeedback({ severity: "error", message: t("feedback.saveError") });
    }
  };

  const needsSource = mode === "create";
  const canSave = !!name.trim() && !loadingSource && !submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <DialogTitle>
        {t(mode === "edit" ? "editor.editTitle" : "editor.createTitle")}
      </DialogTitle>
      {/* il margine orizzontale stacca i divider dai bordi della modale,
          lasciando il contenuto allineato ai 24px di titolo e azioni */}
      <DialogContent
        dividers
        sx={{
          mx: 3,
          px: 0,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("editor.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            size="small"
          />

          {needsSource && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="brand-asset-source-label">
                    {t("editor.startFrom")}
                  </InputLabel>
                  <Select
                    labelId="brand-asset-source-label"
                    label={t("editor.startFrom")}
                    value={sourceId}
                    onChange={(e) => handlePickSource(Number(e.target.value))}
                  >
                    {sourceModels.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <input
                  ref={zipInputRef}
                  type="file"
                  accept=".zip,application/zip"
                  hidden
                  onChange={(e) => {
                    handlePickZipFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <ButtonSecondary
                  size="md"
                  disabled={loadingSource}
                  onClick={() => zipInputRef.current?.click()}
                  startIcon={<Upload size={16} />}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  {t("editor.uploadZip")}
                </ButtonSecondary>
              </Box>
            </Box>
          )}

          <TabsCst
            tabs={[
              { key: "colors", label: String(t("tabs.colors")) },
              { key: "logos", label: String(t("tabs.logos")) },
              { key: "parameters", label: String(t("tabs.parameters")) },
            ]}
            activeKey={tab}
            onChange={(key) => setTab(key as BrandAssetTab)}
          />

          {loadingSource ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {tab === "colors" && (
                <ColorsPanel
                  colors={content.colors}
                  originalColors={originalContent.colors}
                  canDelete={isGlobalScope}
                  onChange={setColor}
                  onDelete={removeColor}
                />
              )}
              {tab === "logos" && (
                <LogosPanel logos={content.logos} onChange={setLogo} />
              )}
              {tab === "parameters" && (
                <ParametersPanel parameters={content.parameters} />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <ButtonSecondary id="brand-asset-cancel" size="md" onClick={onClose}>
          {t("cancel")}
        </ButtonSecondary>
        {!isGlobalScope && (
          <ButtonSecondary
            id="brand-asset-save-global"
            size="md"
            onClick={handleSaveToGlobal}
            disabled={!canSave}
          >
            {t("editor.saveToGlobal")}
          </ButtonSecondary>
        )}
        <ButtonPrimary
          id="brand-asset-save"
          size="md"
          onClick={handleSave}
          disabled={!canSave}
        >
          {t("saveButton")}
        </ButtonPrimary>
      </DialogActions>

      <Snackbar
        open={!!feedback}
        autoHideDuration={feedback?.severity === "error" ? 10000 : 4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {feedback ? (
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Dialog>
  );
};

export default ModelEditorDialog;
